import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { EmptyState } from '@/components/EmptyState';
import { AlertTriangle, Plus, User, MapPin, Calendar, Activity, Camera, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { bozpIncidents, hazardousZones, buildings } from '@/data/mockData';
import { getMergedIncidents, addIncident } from '@/data/incidentStore';

function getSeverityColor(severity) {
  if (severity === 'low') return 'default';
  if (severity === 'medium') return 'warning';
  if (severity === 'high') return 'destructive';
  return 'default';
}

function getRiskColor(level) {
  if (level === 'low') return 'success';
  if (level === 'medium') return 'warning';
  if (level === 'high') return 'destructive';
  return 'default';
}

function IncidentCard({ incident }) {
  const { t } = useLanguage();
  const building = buildings.find(b => b.id === incident.location);

  return (
    <Card className="shadow-soft hover:shadow-elevated transition-all">
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={getSeverityColor(incident.severity)}>
                  {t(incident.severity)} severity
                </Badge>
                <Badge variant="outline">{incident.type}</Badge>
              </div>
              <p className="text-sm text-foreground mb-3">{incident.description}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  <span>{building?.name} - {incident.zone}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(incident.reportedDate).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <User className="w-3 h-3" />
                  <span>Reported by: {incident.reportedBy}</span>
                </div>
              </div>
            </div>
            <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${
              incident.severity === 'high' ? 'text-destructive' :
              incident.severity === 'medium' ? 'text-warning' :
              'text-muted-foreground'
            }`} />
          </div>

          {incident.injuredPerson && (
            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Injured person:</p>
              <p className="text-sm font-medium text-foreground">{incident.injuredPerson}</p>
              <div className="flex gap-4 mt-2">
                <div>
                  <p className="text-xs text-muted-foreground">Medical attention</p>
                  <p className="text-sm font-medium">{incident.medicalAttention ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Days off</p>
                  <p className="text-sm font-medium">{incident.daysOff}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

function HazardousZoneCard({ zone }) {
  const { t } = useLanguage();
  const building = buildings.find(b => b.id === zone.buildingId);

  return (
    <Card className={`shadow-soft border-2 ${
      zone.riskLevel === 'high' ? 'border-destructive' :
      zone.riskLevel === 'medium' ? 'border-warning' :
      'border-success'
    }`}>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-foreground mb-1">{zone.name}</h3>
              <p className="text-sm text-muted-foreground">{building?.name}</p>
            </div>
            <Badge variant={getRiskColor(zone.riskLevel)}>
              {zone.riskLevel} risk
            </Badge>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2">Identified Hazards:</p>
            <div className="flex flex-wrap gap-2">
              {zone.hazards.map((hazard, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {hazard}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2">Required PPE:</p>
            <div className="flex flex-wrap gap-2">
              {zone.requiredPPE.map((ppe, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {ppe}
                </Badge>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border flex justify-between items-center">
            <div>
              <p className="text-xs text-muted-foreground">Last inspection</p>
              <p className="text-sm font-medium text-foreground">
                {new Date(zone.lastInspection).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const INCIDENT_TYPES = [
  { value: 'minor-injury', label: 'Minor injury' },
  { value: 'near-miss', label: 'Near miss' },
  { value: 'equipment-failure', label: 'Equipment failure' },
  { value: 'slip-trip-fall', label: 'Slip / Trip / Fall' },
  { value: 'other', label: 'Other' },
];

export const BOZPPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('incidents');
  const [zonesView, setZonesView] = useState('list'); // 'list' | 'map'
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [form, setForm] = useState({
    type: '',
    severity: 'medium',
    location: '',
    zone: '',
    description: '',
    reportedDate: new Date().toISOString().slice(0, 16),
    photo: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allIncidents = getMergedIncidents(bozpIncidents);
  const incidentsByMonth = allIncidents.length;
  const highRiskZones = hazardousZones.filter(z => z.riskLevel === 'high').length;
  const totalZones = hazardousZones.length;

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setForm(prev => ({ ...prev, photo: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleReportSubmit = (e) => {
    e.preventDefault();
    if (!form.type || !form.location || !form.zone || !form.description.trim()) {
      toast.error('Vyplňte typ, místo, zónu a popis.');
      return;
    }
    setIsSubmitting(true);
    addIncident({
      type: form.type,
      severity: form.severity,
      location: form.location,
      zone: form.zone.trim(),
      description: form.description.trim(),
      reportedDate: new Date(form.reportedDate).toISOString(),
      reportedBy: user?.name || 'Unknown',
      injuredPerson: null,
      medicalAttention: false,
      daysOff: 0,
      photo: form.photo || null,
    });
    toast.success('Incident nahlášen.');
    setForm({ type: '', severity: 'medium', location: '', zone: '', description: '', reportedDate: new Date().toISOString().slice(0, 16), photo: null });
    setReportDialogOpen(false);
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('bozpManagement')}</h1>
          <p className="text-muted-foreground">
            {t('safetyIncidents')} & {t('hazardousZones')}
          </p>
        </div>
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              {t('reportIncident')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('reportIncident')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleReportSubmit} className="space-y-4">
              <div>
                <Label>{t('incidentType')}</Label>
                <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Vyberte typ" /></SelectTrigger>
                  <SelectContent>
                    {INCIDENT_TYPES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Závažnost</Label>
                <Select value={form.severity} onValueChange={(v) => setForm(prev => ({ ...prev, severity: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Budova</Label>
                <Select value={form.location} onValueChange={(v) => setForm(prev => ({ ...prev, location: v }))}>
                  <SelectTrigger><SelectValue placeholder="Vyberte budovu" /></SelectTrigger>
                  <SelectContent>
                    {buildings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zóna / místo</Label>
                <Input
                  value={form.zone}
                  onChange={(e) => setForm(prev => ({ ...prev, zone: e.target.value }))}
                  placeholder="např. Výrobní linka 2"
                />
              </div>
              <div>
                <Label>Popis</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Popis incidentu..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Datum a čas</Label>
                <Input
                  type="datetime-local"
                  value={form.reportedDate}
                  onChange={(e) => setForm(prev => ({ ...prev, reportedDate: e.target.value }))}
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Fotka (volitelné)
                </Label>
                <Input type="file" accept="image/*" onChange={handlePhotoChange} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setReportDialogOpen(false)}>Zrušit</Button>
                <Button type="submit" disabled={isSubmitting}>Odeslat</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-soft border-warning border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Incidents</p>
                <p className="text-3xl font-bold text-foreground">{allIncidents.length}</p>
              </div>
              <div className="p-3 bg-warning-light rounded-lg">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">This Month</p>
                <p className="text-3xl font-bold text-foreground">{incidentsByMonth}</p>
              </div>
              <div className="p-3 bg-info-light rounded-lg">
                <Activity className="w-6 h-6 text-info" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft border-destructive border-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">High Risk Zones</p>
                <p className="text-3xl font-bold text-foreground">{highRiskZones}</p>
              </div>
              <div className="p-3 bg-destructive-light rounded-lg">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Zones</p>
                <p className="text-3xl font-bold text-foreground">{totalZones}</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <MapPin className="w-6 h-6 text-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('incidents')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'incidents'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('incidents')}
        </button>
        <button
          onClick={() => setActiveTab('zones')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'zones'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('hazardousZones')}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'incidents' && (
        allIncidents.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title={t('noData')}
            description="Zatím nebyly nahlášeny žádné incidenty."
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {allIncidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} />
            ))}
          </div>
        )
      )}

      {activeTab === 'zones' && (
        <div className="space-y-6">
          <div className="flex gap-2">
            <Button
              variant={zonesView === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setZonesView('list')}
            >
              Seznam
            </Button>
            <Button
              variant={zonesView === 'map' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setZonesView('map')}
            >
              Mapa zón
            </Button>
          </div>
          {zonesView === 'list' ? (
            hazardousZones.length === 0 ? (
              <EmptyState
                icon={MapPin}
                title={t('noData')}
                description="Žádné nebezpečné zóny nejsou evidovány."
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {hazardousZones.map((zone) => (
                  <HazardousZoneCard key={zone.id} zone={zone} />
                ))}
              </div>
            )
          ) : (
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Vizualizace rizikových zón po budovách
                </CardTitle>
                <CardDescription>
                  Přehled zón po budovách (bez mapového podkladu). Pro skutečnou mapu zón je potřeba integrace s mapovou vrstvou.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {buildings.map((building) => {
                    const buildingZones = hazardousZones.filter(z => z.buildingId === building.id);
                    if (buildingZones.length === 0) return null;
                    return (
                      <div
                        key={building.id}
                        className="rounded-lg border-2 border-border p-4 bg-muted/30"
                      >
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-primary" />
                          {building.name}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {buildingZones.map((zone) => (
                            <Badge
                              key={zone.id}
                              variant={getRiskColor(zone.riskLevel)}
                              className="cursor-default"
                            >
                              {zone.name} ({zone.riskLevel})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
